import { inject, Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';

import { type ActionConfiguratorFormField } from '@blaze-shared/connectors-domain';
import { type ColumnQuery, type DatasetColumn, getColumnKey } from '@blaze-shared/schema-domain';
import { type DataAssignment } from '@blaze-shared/workflow-domain';

import { ActionConfiguratorService } from '../action-configurator.service';

/**
 * Works out what an action node writes when the action is configured to ask the user first.
 *
 * An action can put some of its fields on a dialog rather than filling them in itself. Those
 * fields are then written twice over: the dialog receives the configured value as its starting
 * point, and the action writes back whatever the user made of it.
 */
@Injectable({ providedIn: 'root' })
export class ActionNodeAssignmentsService {
  private readonly actionConfiguratorService = inject(ActionConfiguratorService);

  /**
   * @param shouldGenerateForm whether the action asks the user before writing
   * @param passThroughAssignments the assignments that read the dialog's own output
   * @param defaultAssignments the assignments as the author configured them
   * @throws when a field has no matching assignment, which would silently drop what it collects.
   */
  generateActionNodeAssignments(
    shouldGenerateForm: boolean,
    fieldsToPass: ActionConfiguratorFormField[],
    passThroughAssignments: DataAssignment[],
    defaultAssignments: DataAssignment[],
  ): { assignments: DataAssignment[]; dialogAssignments?: DataAssignment[] } {
    if (!shouldGenerateForm) {
      return { assignments: defaultAssignments };
    }

    // The dialog opens on the configured values of the fields it shows.
    const dialogAssignments = fieldsToPass
      .filter((field) => !!field.showOnForm)
      .map((field) => matchAssignment(defaultAssignments, field));

    // A field on the dialog is written with what the user entered; the rest keep their value.
    const assignments = fieldsToPass.map((field) =>
      matchAssignment(field.showOnForm ? passThroughAssignments : defaultAssignments, field),
    );

    return { assignments, dialogAssignments };
  }

  /**
   * Which of the node's fields appear on its dialog.
   *
   * The node's own record is authoritative once it exists; before that the dialog is asked what it
   * shows, since the fields were configured there.
   */
  getShowOnFormMap(
    nodeAssignments: ActionConfiguratorFormField[] | undefined,
    nodeDialogAssignments: ActionConfiguratorFormField[] | undefined,
    relatedDialogId: string | undefined,
    nodeColumns: DatasetColumn[] | undefined,
  ): Observable<Record<string, boolean>> {
    const showOnFormMap = nodeDialogAssignments
      ? nodeDialogAssignments.reduce(
          (result, a) => {
            result[a.id ?? a.name] = a.showOnForm;
            return result;
          },
          {} as Record<string, boolean>,
        )
      : undefined;

    // Assignments having values means the node was already configured, dialog or no dialog.
    if (!relatedDialogId || nodeAssignments?.length) {
      return of(showOnFormMap ?? {});
    }
    return showOnFormMap
      ? of(showOnFormMap)
      : this.actionConfiguratorService.fetchShowOnForm(
          nodeColumns?.map<ColumnQuery>(({ id, name }) => ({ id, name })),
          relatedDialogId,
        );
  }
}

function matchAssignment(
  assignments: DataAssignment[],
  field: ActionConfiguratorFormField,
): DataAssignment {
  const assignment = assignments.find((candidate) => candidate.name === getColumnKey(field));
  if (!assignment) {
    throw new Error('Assignment could not be matched');
  }
  return assignment;
}
